import { inject, Injectable } from '@angular/core';
import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { environment } from '@environments/environment';
import { MsalService } from '@azure/msal-angular';

@Injectable({
  providedIn: 'root'
})
export class FrLogService {
  private ignoredTelemetryEndpoints = [
      '/api/healthcheck/internetcheck',
    ];

  private authService = inject(MsalService); 
  appInsights: ApplicationInsights;

  constructor() {
    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: environment.appInsights.instrumentationKey,
        loggingLevelTelemetry: 1, 
        enableAutoRouteTracking: false,
        disableAjaxTracking: environment.appInsights.disableAjaxTracking,
      }
    });
    this.appInsights.loadAppInsights();
    this.addTelemetryInitializer();
  }

  private shouldIgnoreEndpoint(telemetry: ITelemetryItem): boolean {
    const targetUrl = telemetry.baseData?.['data'] || telemetry.baseData?.['target'] || '';
    return this.ignoredTelemetryEndpoints.some(endpoint => targetUrl.includes(endpoint));
  }

  private addTelemetryInitializer() {
    this.appInsights.addTelemetryInitializer((telemetry) => {
      const activeAccount = this.authService.instance?.getActiveAccount();

      if (!telemetry.data) {
        telemetry.data = {};
      }

      const customDimensions = telemetry.data as Record<string, unknown>;

      customDimensions['accountId'] = activeAccount?.homeAccountId ?? 'N/A';
      customDimensions['username'] = activeAccount?.username ?? 'N/A';

      if (telemetry.baseType === 'RemoteDependencyData' && this.shouldIgnoreEndpoint(telemetry)) {
        return false;
      }

      return true;
    });
  }

  logException(exception: Error, severityLevel?: number) {
    this.appInsights.trackException({ exception: exception, severityLevel: severityLevel });
  }

  private logTrace(message: string, properties?: { [key: string]: unknown }) {
    this.appInsights.trackTrace({ message: message, properties: properties });
  }

  logFrError(error: unknown, throwError: boolean = false) {
    console.error(error);

    try {
      const activeAccount = this.authService.instance?.getActiveAccount();
      const accountId = activeAccount?.homeAccountId ?? 'N/A';
      const accountName = activeAccount?.name ?? 'N/A';

      if (error instanceof Error) {
        error.message = `${accountId} | ${accountName} | ${error.message}`;
        this.logException(error);
        this.appInsights.flush();
        if (throwError) {
          throw error;
        }
      } else {
        const message = `${accountId} | ${accountName} | ${String(error)}`;
        this.logException(new Error(message));
        this.appInsights.flush();
        if (throwError) {
          throw new Error(String(error));
        }
      }
    } catch (ex) {
      if (error instanceof Error) {
        this.logException(error);
      } else {
        this.logException(new Error(String(error)));
      }
      this.logException(new Error(`logFrError Exception | ${ex}.`));
    }
  }

  logFrTrace(message: string, properties?: { [key: string]: unknown }) {
    console.info(message, properties);

    this.logTrace(message, properties);
    this.appInsights.flush();
  }
}